import { ActivatedRoute, Router } from '@angular/router'
import { AddressPipe } from '../../../pipes/address.pipe'
import {
    BookingLaunchContext,
    LandingPageLaunchParams,
    VoucherLaunchContext,
} from '../../modal/landing-page-launch.types'
import { BookingModalComponent } from '../../../booking-modal/booking-modal.component'
import { Component, OnDestroy, OnInit } from '@angular/core'
import { LandingPage } from '../../domain/LandingPage'
import { LandingPageLaunchQueryService } from '../../modal/landing-page-launch-query.service'
import { LandingPageModalService } from '../../modal/landing-page-modal.service'
import { LandingPageService } from '../../services/landing-page.service'
import { MetaPixelService } from '../../services/meta-pixel.service'
import { Observable, Subject, catchError, combineLatest, map, mergeMap, of, shareReplay, switchMap, takeUntil } from 'rxjs'
import { Title } from '@angular/platform-browser'
import { Venue } from '../../../domain/Venue'
import { VenueService } from '../../../services/venue.service'
import { WindowService } from '../../../services/window/window.service'
import { environment } from '../../../../environments/environment'

type LandingPageHomeViewModel = {
    landingPage: LandingPage
    launchParams: LandingPageLaunchParams
    bookingContext: BookingLaunchContext | null
    voucherContext: VoucherLaunchContext | null
    venue: Venue | null
    venueColour: string | null
    venueSlug: string | null
    isLegacyVenueLink: boolean
    voucherStorefrontPath: string | null
}

@Component({
    selector: 'app-landing-page-home',
    templateUrl: './home.component.html',
})
export class LandingPageHomeComponent implements OnInit, OnDestroy {

    marketingUrl = environment.marketingUrl
    viewModel$!: Observable<LandingPageHomeViewModel>
    mapUrl$!: Observable<string>
    private hasOpenedInitialModal = false
    private hasMergedRwgUtmParams = false
    protected hasRedirectedToStorefront = false
    private onDestroy$ = new Subject<void>()

    constructor(
        private addressPipe: AddressPipe,
        private route: ActivatedRoute,
        private router: Router,
        private titleService: Title,
        private landingPageService: LandingPageService,
        private launchQueryService: LandingPageLaunchQueryService,
        private landingPageModalService: LandingPageModalService,
        private metaPixelService: MetaPixelService,
        private venueService: VenueService,
        private windowService: WindowService
    ) { }

    ngOnInit() {
        const landingPageContext$ = this.route.paramMap
            .pipe(
                mergeMap(params => {
                    const venueSlug = params.get('venueSlug')
                    if (venueSlug) {
                        return this.landingPageService.getLandingPageBySlug(venueSlug)
                            .pipe(
                                map(landingPage => ({ landingPage, venueSlug }))
                            )
                    }
                    const venueId = params.get('venueId') ?? ''
                    return this.landingPageService.getLandingPage(venueId)
                        .pipe(
                            map(landingPage => ({ landingPage, venueSlug: null }))
                        )
                }),
                shareReplay({ bufferSize: 1, refCount: true })
            )
        const landingPage$ = landingPageContext$
            .pipe(
                map(context => context.landingPage)
            )

        landingPage$
            .pipe(takeUntil(this.onDestroy$))
            .subscribe(landingPage => {
                this.titleService.setTitle(landingPage.displayName)
                this.metaPixelService.configure(landingPage.metaPixelId)
            })

        this.mapUrl$ = landingPage$
            .pipe(
                map(landingPage => this.addressPipe.transform(landingPage.address)),
                map(address => encodeURIComponent(address)),
                map(address => {
                    return 'https://www.google.com/maps/embed/v1/place' +
                        `?key=${environment.googleMapsApiKey}&q=${address}`
                })
            )

        this.viewModel$ = combineLatest([landingPageContext$, this.route.queryParamMap])
            .pipe(
                map(([landingPageContext, queryParamMap]) => {
                    const { landingPage, venueSlug } = landingPageContext
                    const launchParams = this.launchQueryService.fromQueryParamMap(
                        queryParamMap,
                        landingPage.timeZone
                    )
                    const bookingContext = this.launchQueryService.makeBookingContext(landingPage, launchParams)
                    const voucherContext = launchParams.voucherId
                        ? this.launchQueryService.makeVoucherContext(landingPage, launchParams.voucherId)
                        : null
                    return {
                        landingPage,
                        launchParams,
                        bookingContext,
                        voucherContext,
                        venueSlug,
                        isLegacyVenueLink: venueSlug === null,
                    }
                }),
                switchMap(viewModel => {
                    let venue$: Observable<Venue | null>
                    if (viewModel.venueSlug) {
                        venue$ = this.venueService.getWithSlug(viewModel.venueSlug)
                    } else if (viewModel.bookingContext) {
                        venue$ = this.venueService.getWithId(viewModel.bookingContext.venueId)
                    } else {
                        venue$ = of(null)
                    }
                    return venue$
                        .pipe(
                            map(venue => {
                                const venueSlug = viewModel.venueSlug ?? venue?.venueSlug ?? null
                                return {
                                    ...viewModel,
                                    venue,
                                    venueColour: venue?.primaryColour ?? null,
                                    venueSlug,
                                    voucherStorefrontPath: viewModel.landingPage.voucher && venueSlug
                                        ? `/discover/${encodeURIComponent(venueSlug)}/vouchers`
                                        : null,
                                }
                            }),
                            catchError(() => of({
                                ...viewModel,
                                venue: null,
                                venueColour: null,
                                voucherStorefrontPath: null,
                            }))
                        )
                }),
                shareReplay({ bufferSize: 1, refCount: true })
            )

        this.viewModel$
            .pipe(takeUntil(this.onDestroy$))
            .subscribe(viewModel => {
                if (this.redirectToStorefront(viewModel)) {
                    return
                }
                this.mergeRwgUtmParams(viewModel.launchParams)
                this.openInitialModalIfNeeded(viewModel)
            })
    }

    ngOnDestroy() {
        this.onDestroy$.next()
        this.onDestroy$.complete()
    }

    openBookingModal(context: BookingLaunchContext) {
        const modal = this.landingPageModalService.openBookingModal(context)
        const component = modal.componentInstance as BookingModalComponent
        component.bookingConfirmed
            .pipe(takeUntil(this.onDestroy$))
            .subscribe(payload => {
                this.metaPixelService.trackSchedule(payload)
            })
    }

    openVoucherModal(context: VoucherLaunchContext) {
        this.landingPageModalService.openVoucherModal(context)
    }

    private redirectToStorefront(viewModel: LandingPageHomeViewModel): boolean {
        if (!viewModel.venueSlug) {
            return false
        }
        const location = this.windowService.getLocation()
        const storefrontUrl = new URL(
            `/discover/${encodeURIComponent(viewModel.venueSlug)}`,
            environment.venueStorefrontBaseUrl
        )
        storefrontUrl.search = location.search
        storefrontUrl.hash = location.hash
        const isVoucherLink = storefrontUrl.searchParams.getAll('vid').some(voucherId => voucherId.length > 0)
        if (!viewModel.isLegacyVenueLink && !isVoucherLink) {
            return false
        }
        if (this.hasRedirectedToStorefront) {
            return true
        }
        this.hasRedirectedToStorefront = true

        if (isVoucherLink) {
            storefrontUrl.pathname += '/vouchers'
            storefrontUrl.searchParams.delete('vid')
        }
        this.windowService.replaceLocation(storefrontUrl.toString())
        return true
    }

    private mergeRwgUtmParams(params: LandingPageLaunchParams) {
        if (!params.rwgToken || this.hasMergedRwgUtmParams) {
            return
        }
        this.hasMergedRwgUtmParams = true
        this.router.navigate([], {
            relativeTo: this.route,
            queryParams: {
                utm_source: 'rwg',
                utm_medium: 'integration',
                utm_campaign: params.rwgToken,
            },
            queryParamsHandling: 'merge',
            replaceUrl: true,
        })
    }

    private openInitialModalIfNeeded(viewModel: LandingPageHomeViewModel) {
        if (
            this.hasOpenedInitialModal ||
            !viewModel.landingPage.inService ||
            viewModel.venue?.shouldUseVenueStorefrontExperience() ||
            (viewModel.venue && (viewModel.launchParams.paymentIntentId || viewModel.launchParams.setupIntentId))
        ) {
            return
        }
        if (viewModel.voucherContext) {
            this.hasOpenedInitialModal = true
            this.openVoucherModal(viewModel.voucherContext)
            return
        }
        if (
            viewModel.bookingContext &&
            this.launchQueryService.shouldOpenBookingOnLoad(viewModel.launchParams)
        ) {
            this.hasOpenedInitialModal = true
            this.openBookingModal(viewModel.bookingContext)
        }
    }
}
